import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, concatMap, from, map, of, toArray } from 'rxjs';
import {
  Minus,
  Plus,
  ShoppingCart,
  Bell,
  LucideIconData,
} from 'lucide-angular';

import {
  Product,
  AddToCartEvent,
  VARIANT_LABELS,
  LOW_STOCK_THRESHOLD,
  PREPURCHASE_SOLD_OUT_MESSAGE,
  ProductNotFoundError,
  getPrepurchaseStock,
  isEarlyAccessProduct,
  isPrepurchaseSoldOut,
} from '../../../shared/models/product.model';
import { requirementHintText, vipBadgeText } from '../../../shared/perron-points/perron-points-copy';
import {
  EarlyAccessBlockedError,
  EarlyAccessGateRejection,
  earlyAccessThresholdOf,
} from '../../../shared/perron-points/early-access-gate';
import { Category } from '../../../shared/models/category.model';
import {
  formatPrice,
  formatOriginalPrice,
  calcDiscountPercent,
  stripHtml,
  isSingleProduct,
  displayLanguage,
  formatCondition,
  getConditionTone,
  formatPowerToughness,
  hasGameMechanics,
  splitSubtype,
} from '../../../shared/utils/product.utils';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/ui/breadcrumb/breadcrumb.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { PpCoinComponent } from '../../../shared/ui/pp-coin/pp-coin.component';
import { VariantBadgeComponent } from '../../../shared/ui/variant-badge/variant-badge.component';
import { IconComponent } from '../../../shared/ui/icon/icon.component';
import { InputComponent } from '../../../shared/ui/input/input.component';
import { ProductZoomModalComponent } from '../../../shared/ui/product-zoom-modal/product-zoom-modal.component';
import { ImageLightboxComponent } from '../../../shared/ui/image-lightbox/image-lightbox.component';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { WishlistButtonComponent } from '../../../shared/ui/wishlist-button/wishlist-button.component';

import { ImageGalleryComponent } from './components/image-gallery/image-gallery.component';
import { ProductTabsComponent } from './components/product-tabs/product-tabs.component';
import { RelatedProductsComponent } from './components/related-products/related-products.component';
import { FrequentlyBoughtTogetherComponent } from './components/frequently-bought-together/frequently-bought-together.component';
import { ProductDetailSkeletonComponent } from './components/product-detail-skeleton/product-detail-skeleton.component';
import { ProductNotFoundComponent } from './components/product-not-found/product-not-found.component';

import { SeoService } from '../../../shared/services/seo.service';
import { absoluteUrl, buildBreadcrumbJsonLd } from '../../../shared/utils/seo.utils';
import { CatalogService } from '../../catalog/data/catalog.service';
import {
  deriveProductCategorySlug,
  deriveProductLeafCategoryLink,
  deriveProductRootCategoryLink,
  resolveProductBreadcrumbPath,
} from '../../catalog/data/catalog-mappers';
import { CategoryService } from '../../../shared/services/category.service';
import { CartDrawerService } from '../../../shared/services/cart-drawer.service';
import { CartService } from '../../../shared/services/cart.service';
import { PrepurchaseModalService } from '../../../shared/services/prepurchase-modal.service';
import { StockAlertStore } from '../../../shared/services/stock-alert.store';
import { StockAlertModalService } from '../../../shared/services/stock-alert-modal.service';
import { AuthService } from '../../../shared/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { PointsService } from '../../../shared/services/points.service';
import { StockAlertError } from '../../../shared/models/stock-alert.model';
import { CartStockError } from '../../../shared/models/cart.model';
import { cartErrorMessage } from '../../../shared/utils/cart.utils';

type CtaMode = 'add' | 'reserve' | 'reserve_sold_out' | 'notify';
type NotifyStatus = 'idle' | 'pending' | 'success' | 'already_subscribed' | 'prepurchase' | 'error';
type AddStatus = 'idle' | 'pending' | 'success' | 'stock_error' | 'error';

/** Cache `id → slug` en sessionStorage para resolver slugs renombrados. */
const SLUG_CACHE_KEY = 'perron.slug-cache';

@Component({
  selector: 'app-product-detail-page',
  imports: [
    RouterLink,
    BreadcrumbComponent,
    ButtonComponent,
    BadgeComponent,
    PpCoinComponent,
    VariantBadgeComponent,
    IconComponent,
    InputComponent,
    ProductZoomModalComponent,
    ImageLightboxComponent,
    TooltipDirective,
    WishlistButtonComponent,
    ImageGalleryComponent,
    ProductTabsComponent,
    RelatedProductsComponent,
    FrequentlyBoughtTogetherComponent,
    ProductDetailSkeletonComponent,
    ProductNotFoundComponent,
  ],
  templateUrl: './product-detail-page.component.html',
  styleUrl: './product-detail-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly seoService = inject(SeoService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  private readonly catalogService = inject(CatalogService);
  private readonly categoryService = inject(CategoryService);
  private readonly cartService = inject(CartService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly prepurchaseModalService = inject(PrepurchaseModalService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly stockAlertModal = inject(StockAlertModalService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly pointsService = inject(PointsService);

  // Icons
  protected readonly minusIcon: LucideIconData = Minus;
  protected readonly plusIcon: LucideIconData = Plus;
  protected readonly cartIcon: LucideIconData = ShoppingCart;
  protected readonly bellIcon: LucideIconData = Bell;

  // Árbol cacheado de categorías raíz; lo usamos para resolver el slug
  // canónico del producto (breadcrumbs + redirect canónico). El cache vive
  // en `CategoryService.getRootCategoriesCached()` y comparte payload SSR vía
  // TransferState, así que en hidratación arranca poblado.
  private readonly rootsSig = toSignal(this.categoryService.getRootCategoriesCached(), {
    initialValue: [] as Category[],
  });

  // State
  protected readonly product = signal<Product | null>(null);
  protected readonly loading = signal(true);
  protected readonly notFound = signal(false);
  protected readonly notFoundReason = signal<'not_found' | 'disabled'>('not_found');
  protected readonly quantity = signal(1);
  protected readonly zoomProduct = signal<Product | null>(null);
  protected readonly lightboxOpen = signal(false);
  protected readonly selectedImageIndex = signal(0);
  protected readonly stickyBarVisible = signal(false);
  protected readonly notifyEmail = signal('');
  protected readonly notifyStatus = signal<NotifyStatus>('idle');
  protected readonly addStatus = signal<AddStatus>('idle');
  protected readonly addErrorMessage = signal<string | null>(null);
  protected readonly bundleAdding = signal(false);
  /** Suma optimista al contador de suscriptores tras un alta exitosa, para
   * reflejar el cambio sin recargar el producto. Se resetea al cambiar de slug
   * en `resetTransientState()`. */
  protected readonly optimisticExtraSubscribers = signal(0);
  /** El `stock_alert.has_alert` que trajo el producto quedó obsoleto: en esta
   * vista se confirmó la cancelación de su aviso. Sin esto, la ficha seguía
   * diciendo «Te avisaremos» mientras el modal y las cards ya ofrecían
   * «Avisarme». Se resetea al cambiar de slug en `resetTransientState()`.
   * Limitación conocida (aceptada en W3, tarea [D3]): si una baja sigue en
   * vuelo tras A→B→A, al resolverse no marca esta visita (su operación quedó
   * obsoleta) y la ficha conserva el `has_alert` de su carga hasta recargar,
   * aunque el store, la campana y las cards ya reflejen la baja. */
  private readonly serverAlertCancelled = signal(false);
  /**
   * Secuencia de las operaciones de aviso (alta y baja) con efecto en ESTA
   * vista. El componente se reutiliza entre fichas (cambia el `:productSlug`,
   * no se destruye) y las peticiones siguen vivas en el servicio global
   * (`StockAlertModalService`: baja y alta), así que su respuesta puede
   * llegar con otra ficha en pantalla (A→B) o con la misma ficha en otra visita
   * (A→B→A). Cada operación toma un número; `resetTransientState()` y la
   * destrucción de la vista lo incrementan, y una operación nueva invalida a la
   * anterior. Un callback solo toca la vista si su número sigue siendo el
   * último y la ficha sigue siendo la suya. El store se actualiza siempre.
   */
  private notifyOpSeq = 0;

  // Computed: stock + estados
  protected readonly isPreSale = computed(() => {
    const p = this.product();
    return !!p
      && (p.prepurchase === true || p.isPreSale === true)
      && getPrepurchaseStock(p) > 0;
  });
  /**
   * Identidad de precompra SIN mirar cupos (N45): `isPreSale()` exige cupos > 0,
   * así que una precompra agotada con stock físico > 0 no lo cumple. El bundle
   * FBT necesita excluir cualquier precompra principal, tenga cupos o no.
   */
  protected readonly isPrepurchaseProduct = computed(() => {
    const p = this.product();
    return !!p && (p.prepurchase === true || p.isPreSale === true);
  });
  protected readonly isOutOfStock = computed(() => {
    const p = this.product();
    return !!p && (p.stock ?? 0) === 0 && !this.isPreSale();
  });
  protected readonly isLowStock = computed(() => {
    const s = this.product()?.stock ?? 0;
    return s > 0 && s <= LOW_STOCK_THRESHOLD;
  });
  /** Badge «Acceso VIP» (Perrón Points) — desde R7 no depende de precompra. */
  protected readonly isEarlyAccess = computed(() => isEarlyAccessProduct(this.product()));

  /**
   * Umbral efectivo del producto: el resuelto por el servidor y, si el payload
   * no lo trae, el último umbral conocido de usuario o el fallback.
   */
  protected readonly eaThreshold = computed(
    () =>
      earlyAccessThresholdOf(this.product(), this.pointsService.displayThreshold())
      ?? this.pointsService.displayThreshold(),
  );

  /** Badge de identidad VIP, con la variante de umbral 0. */
  protected readonly eaBadgeText = computed(() => vipBadgeText(this.eaThreshold()));

  /** Subtexto de requisito bajo el título, con la variante de umbral 0. */
  protected readonly eaRequirementHint = computed(() => requirementHintText(this.eaThreshold()));
  /** Precompra con cupos a 0 — separado de `isOutOfStock` (que excluye
   * pre-sales) para no romper el resto de derived state (badges, notify
   * counter, JSON-LD). Solo lo usa el `ctaMode` para enrutar al CTA atenuado. */
  protected readonly isPrepurchaseSoldOutSig = computed(() =>
    isPrepurchaseSoldOut(this.product()),
  );
  protected readonly ctaMode = computed<CtaMode>(() => {
    // Importante: precompra agotada va ANTES que `notify` para no caer en
    // el flujo de "Avisarme" — las precompras tienen su propio mensaje.
    if (this.isPrepurchaseSoldOutSig()) return 'reserve_sold_out';
    if (this.isOutOfStock()) return 'notify';
    if (this.isPreSale()) return 'reserve';
    return 'add';
  });
  protected readonly prepurchaseSoldOutMessage = PREPURCHASE_SOLD_OUT_MESSAGE;
  protected readonly maxQty = computed(() => {
    const p = this.product();
    if (!p) return 1;
    const stock = this.isPreSale() ? getPrepurchaseStock(p) : (p.stock ?? 0);
    // El backend usa `0`/null/undefined indistintamente como "sin tope por orden".
    // `?? Infinity` no atrapa el `0`, por lo que normalizamos vía Number() y
    // tratamos cualquier valor no positivo como sin límite (el backend revalida).
    const rawPerOrder = Number(p.max_units_per_order);
    const perOrder =
      Number.isFinite(rawPerOrder) && rawPerOrder > 0 ? rawPerOrder : Infinity;
    return Math.max(1, Math.min(Number(stock) || 0, perOrder));
  });

  protected readonly isSingle = computed(() => {
    const p = this.product();
    // Mientras carga no hay producto: `false` evita que la fila de badges
    // parpadee antes de saber si es una carta.
    return !!p && isSingleProduct(p);
  });

  protected readonly isAccessory = computed(() =>
    this.product()?.productType === 'accessory',
  );

  /**
   * Sugeridos del bundle "Frecuentemente comprados juntos". Filtramos fuera
   * los que no se pueden comprar (stock 0 y no pre-venta) para no mostrar
   * checkboxes inertes. Si queda la lista vacía, la sección entera se oculta
   * en el template.
   */
  protected readonly fbtSuggestions = computed<Product[]>(() => {
    const list = this.product()?.frequently_bought_together ?? [];
    // Excluye agotados (regulares y precompras sin cupos) — el back no
    // envía precio para esos y el bundle no podría calcular el total.
    return list.filter((p) =>
      p.isPreSale === true
        ? getPrepurchaseStock(p) > 0
        : (p.stock ?? 0) > 0,
    );
  });

  /**
   * Sugeridos que entran al bundle FBT (D26): solo regulares con stock. Una
   * precompra no puede ir al carrito normal (la API responde 422); sigue en
   * `fbtSuggestions`, que alimenta el carrusel, cuyas tarjetas desvían a reserva.
   */
  protected readonly fbtBundleItems = computed<Product[]>(() =>
    this.fbtSuggestions().filter((p) => p.isPreSale !== true && (p.stock ?? 0) > 0),
  );

  protected readonly formattedPrice = computed(() => {
    const p = this.product();
    return p ? formatPrice(p) : '';
  });

  protected readonly formattedOriginalPrice = computed(() => {
    const p = this.product();
    return p ? formatOriginalPrice(p) : '';
  });

  protected readonly formattedLanguage = computed(() => {
    const p = this.product();
    return p ? displayLanguage(p) : null;
  });

  protected readonly galleryImages = computed<string[]>(() => {
    const p = this.product();
    if (!p) return [];
    const imgs = p.images;
    if (imgs && imgs.length > 0) {
      return imgs
        .map((img) => (typeof img === 'string' ? img : ''))
        .filter((s): s is string => !!s);
    }
    return p.imageUrl ? [p.imageUrl] : [];
  });

  protected readonly discountPercent = computed(() => {
    const p = this.product();
    return p ? calcDiscountPercent(p) : 0;
  });

  protected readonly variantLabel = computed(() => {
    const v = this.product()?.variant;
    return v ? VARIANT_LABELS[v] : '';
  });

  protected readonly conditionLabel = computed(() => {
    const c = this.product()?.condition;
    return c ? formatCondition(c) : null;
  });

  protected readonly conditionTone = computed(() => {
    const c = this.product()?.condition;
    return c ? getConditionTone(c) : null;
  });

  /**
   * Mecánicas de juego (OP/MTG): controla la sección dedicada bajo el bloque
   * de variante. Cierra completa para productos Pokémon, legacy y no-singles.
   */
  protected readonly hasGameStats = computed(() => {
    const p = this.product();
    return !!p && hasGameMechanics(p);
  });

  protected readonly subtypeChips = computed(() => splitSubtype(this.product()?.subtype));

  protected readonly powerToughness = computed(() =>
    formatPowerToughness(this.product()?.power, this.product()?.toughness),
  );

  protected readonly powerToughnessLabel = computed(() =>
    this.product()?.toughness ? 'P/T' : 'Poder',
  );

  /**
   * Token semántico para el dot del chip de color (OP). 6 colores canónicos
   * mapean a tokens del DS; cualquier otro → "other" (dot neutro).
   */
  protected readonly colorChipKey = computed<string>(() => {
    const c = this.product()?.color?.toLowerCase().trim();
    if (!c) return 'other';
    if (['yellow', 'red', 'blue', 'green', 'black', 'purple'].includes(c)) return c;
    return 'other';
  });

  /**
   * Breadcrumb basado SIEMPRE en la categoría real del producto, no en el
   * `:category` de la URL (que puede ser virtual o inválido — p. ej. `todos`
   * o un listing que no contiene este producto). El path se deriva del árbol
   * cacheado raíz → hoja vía `resolveProductBreadcrumbPath`. Si el cache aún
   * no está poblado, el path sale vacío y el breadcrumb se reduce a
   * `Inicio / Tienda / <Producto>`; el `computed` re-evalúa al llegar el
   * árbol.
   */
  protected readonly breadcrumbs = computed<BreadcrumbItem[]>(() => {
    const p = this.product();
    if (!p) return [];

    const path = resolveProductBreadcrumbPath(p, this.rootsSig());
    const items: BreadcrumbItem[] = [
      { label: 'Inicio', route: '/' },
      { label: 'Tienda', route: '/tienda' },
    ];
    for (const node of path) {
      items.push({ label: node.name, route: `/tienda/${node.slug}` });
    }
    items.push({ label: p.name, route: null });
    return items;
  });

  /**
   * CTA al final del carrusel de productos relacionados ("Ver más productos
   * de X"). Prefiere la raíz del árbol (`parent`) sobre la subcategoría hoja
   * para que la URL apunte al listing principal — mismo criterio que
   * `deriveProductCategorySlug`. Devuelve `null` si el producto no trae
   * `categories[]`; en ese caso el carrusel no renderiza el slot final.
   */
  protected readonly categoryCta = computed<{ label: string; link: string } | null>(() => {
    const cat = this.product()?.categories?.[0];
    if (!cat) return null;
    const target = cat.parent ?? cat;
    return {
      label: `Ver más productos de ${target.name}`,
      link: `/tienda/${target.slug}`,
    };
  });

  /** Link al listing de la categoría raíz (juego), para hacer clicable `game`. */
  protected readonly gameLink = computed<string | null>(() => {
    const p = this.product();
    return p ? deriveProductRootCategoryLink(p) : null;
  });

  /** Link al listing de la subcategoría hoja (set/expansión), para `setName`. */
  protected readonly setLink = computed<string | null>(() => {
    const p = this.product();
    return p ? deriveProductLeafCategoryLink(p) : null;
  });

  protected readonly canDecrement = computed(() => this.quantity() > 1);
  protected readonly canIncrement = computed(() => this.quantity() < this.maxQty());

  protected readonly subscriberCount = computed(() => {
    const meta = this.product()?.stock_alert;
    return (meta?.subscriber_count ?? 0) + this.optimisticExtraSubscribers();
  });
  /** Alerta guardada en localStorage para este producto en este navegador.
   * Permite mantener la confirmación visible tras un F5 sin depender de
   * `has_alert` del backend (que solo aplica a sesión autenticada). */
  protected readonly locallySubscribed = computed(() =>
    this.stockAlertStore.hasAlert(this.product()?.id),
  );
  /** Alta de este producto en vuelo, desde la ficha o desde el modal (candado
   * compartido en `StockAlertModalService`): «Avisarme» se deshabilita. */
  protected readonly altaInFlight = computed(() =>
    this.stockAlertModal.isSubscribing(this.product()?.id),
  );
  /**
   * Campana del quickview: SOLO el store, igual que la card de relacionados.
   * `has_alert` del backend no vale aquí: sin token local, el «Ya no me
   * avises» del modal no podría cancelar nada.
   */
  protected readonly zoomNotifyActive = computed(() =>
    this.stockAlertStore.hasAlert(this.zoomProduct()?.id),
  );
  /** `has_alert` del servidor, salvo que en esta vista se haya confirmado ya
   * la cancelación de ese aviso. */
  private readonly serverHasAlert = computed(
    () => this.product()?.stock_alert?.has_alert === true && !this.serverAlertCancelled(),
  );
  protected readonly alreadySubscribed = computed(() => {
    return this.serverHasAlert()
      || this.locallySubscribed()
      || this.notifyStatus() === 'already_subscribed'
      || this.notifyStatus() === 'success';
  });

  @ViewChild('priceSection') private priceSectionRef!: ElementRef<HTMLElement>;

  // Track del último (productId → canonicalSlug) ya redirigido, para no
  // intentar redirigir dos veces si el árbol cambia o si el producto se
  // re-fetchea (p. ej. tras un stock_error).
  private lastRedirectedKey: string | null = null;

  constructor() {
    // Cargar producto desde el slug de la URL. Cada cambio de `:productSlug`
    // resetea estado y scroll para evitar ver el flip/qty del producto anterior.
    this.route.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      const slug = params['productSlug'] as string;
      this.resetTransientState();
      this.loadProduct(slug);
    });
    // Al salir de la PDP, toda operación de aviso en vuelo deja de ser de esta vista.
    this.destroyRef.onDestroy(() => this.notifyOpSeq++);

    // Redirect canónico: cuando tengamos producto + árbol cacheado, comparar
    // el slug de URL contra el slug canónico derivado del producto. Si difieren,
    // sustituir la URL con `replaceUrl: true` (no añade entrada al historial).
    // Sólo en browser — en SSR la URL ya viene del cliente y un redirect aquí
    // sería un round-trip extra inútil.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const p = this.product();
      const roots = this.rootsSig();
      if (!p || roots.length === 0 || !p.slug) return;
      const canonical = deriveProductCategorySlug(p, roots);
      if (!canonical) return;
      const urlCategory = this.route.snapshot.params['category'] as string | undefined;
      if (urlCategory === canonical) return;
      const key = `${p.id}|${canonical}`;
      if (this.lastRedirectedKey === key) return;
      this.lastRedirectedKey = key;
      this.router.navigate(['/tienda', canonical, p.slug], { replaceUrl: true });
    });

    // Email por defecto si hay sesión.
    const email = this.authService.currentUser()?.email;
    if (email) this.notifyEmail.set(email);

    // Sticky bar observer (mobile)
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;

      requestAnimationFrame(() => {
        const el = this.priceSectionRef?.nativeElement;
        if (!el) return;

        const observer = new IntersectionObserver(
          ([entry]) => {
            this.stickyBarVisible.set(!entry.isIntersecting);
          },
          { threshold: 0 },
        );
        observer.observe(el);
        this.destroyRef.onDestroy(() => observer.disconnect());
      });
    });
  }

  // ── Actions ──────────────────────────────────────────────────
  protected incrementQty(): void {
    if (this.canIncrement()) this.quantity.update((q) => q + 1);
  }

  protected decrementQty(): void {
    if (this.canDecrement()) this.quantity.update((q) => q - 1);
  }

  /**
   * CTA "Reservar" para productos en precompra. Abre el modal de precompra
   * con la cantidad seleccionada y NO toca el carrito. `addStatus` no aplica
   * aquí porque el estado de submit vive dentro del propio modal.
   */
  protected onReserve(): void {
    const p = this.product();
    if (!p) return;
    this.prepurchaseModalService.open(p, this.quantity());
  }

  protected onPrepurchaseSoldOutClick(): void {
    this.toastService.show({
      type: 'info',
      message: PREPURCHASE_SOLD_OUT_MESSAGE,
    });
  }

  protected onAddToCart(): void {
    const p = this.product();
    if (!p || this.addStatus() === 'pending') return;

    // Defensa: si por algún motivo este CTA se dispara para un producto en
    // precompra (p.ej. un click muy rápido antes de que ctaMode se reevalúe),
    // redirigimos al flujo correcto sin tocar el carrito.
    if (p.isPreSale) {
      this.onReserve();
      return;
    }

    this.addStatus.set('pending');
    this.addErrorMessage.set(null);

    this.cartService.addLine(Number(p.id), this.quantity(), p).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.addStatus.set('success');
        this.cartDrawerService.open();
      },
      error: (err) => {
        // Bloqueo de Acceso VIP: el diálogo ya explica el requisito, así que
        // el CTA solo tiene que soltar el estado `pending`.
        if (err instanceof EarlyAccessBlockedError) {
          this.addStatus.set('idle');
          return;
        }
        if (err instanceof EarlyAccessGateRejection) {
          this.addStatus.set('error');
          this.addErrorMessage.set(err.gate.message);
          return;
        }
        if (err instanceof CartStockError) {
          this.addStatus.set('stock_error');
          this.addErrorMessage.set(err.message);
          // Refetch el producto para traer stock actualizado (alguien compró mientras).
          const slug = this.route.snapshot.params['productSlug'] as string | undefined;
          if (slug) this.catalogService.getProductBySlug(slug).subscribe({
            next: (fresh) => this.product.set(fresh),
            error: () => void 0,
          });
        } else {
          this.addStatus.set('error');
          this.addErrorMessage.set('No pudimos agregar el producto. Intenta de nuevo.');
        }
      },
    });
  }

  protected onNotifyRequest(): void {
    const p = this.product();
    // Con un alta de este producto ya en vuelo (aquí o en el modal) no sale
    // otro POST: el botón está deshabilitado y la respuesta llegará al store.
    if (!p || this.notifyStatus() === 'pending' || this.altaInFlight()) return;

    // Si el backend ya marca que este cliente está suscrito, no volvemos a pegar.
    if (this.serverHasAlert()) {
      this.notifyStatus.set('already_subscribed');
      return;
    }

    const email = (this.notifyEmail() || this.authService.currentUser()?.email || '').trim();
    if (!email) {
      this.notifyStatus.set('error');
      return;
    }

    this.notifyStatus.set('pending');
    // Misma secuencia que la baja (`notifyOpSeq`): la respuesta puede llegar con
    // otra ficha en pantalla o en otra visita de esta. Comparten contador a
    // propósito: solo aplica a la vista la última operación de aviso.
    const op = ++this.notifyOpSeq;
    const productId = Number(p.id);
    const isCurrent = () => op === this.notifyOpSeq && Number(this.product()?.id) === productId;
    // El POST lo hace el servicio compartido con el modal (`requestAlert`):
    // un alta en vuelo por producto, sin abortar al salir de la ficha, y el
    // token al store SIEMPRE. Aquí solo quedan los efectos de esta vista, que
    // se descartan si la operación quedó obsoleta.
    const started = this.stockAlertModal.requestAlert(productId, email, {
      onSuccess: () => {
        if (!isCurrent()) return;
        this.optimisticExtraSubscribers.update((n) => n + 1);
        this.notifyStatus.set('success');
        this.toastService.show({
          type: 'success',
          message: 'Te avisaremos cuando esté disponible.',
        });
      },
      onError: (err) => {
        if (!isCurrent()) return;
        if (err instanceof StockAlertError) {
          if (err.reason === 'already_subscribed') {
            this.notifyStatus.set('already_subscribed');
            return;
          }
          if (err.reason === 'product_is_prepurchase') {
            this.notifyStatus.set('prepurchase');
            return;
          }
        }
        this.notifyStatus.set('error');
      },
    });
    // Otra superficie ganó la carrera entre la comprobación y el POST: esta
    // vista no lanzó nada y se queda en «enviando» vía `altaInFlight`.
    if (!started && isCurrent()) this.notifyStatus.set('idle');
  }

  protected onZoomOpen(): void {
    // Click en la imagen de la ficha → lightbox puro de imagen (no quick-view).
    if (this.product()) this.lightboxOpen.set(true);
  }

  /**
   * Maneja el zoomClick del image-gallery: recibe el índice de la imagen que
   * el usuario está viendo en el slot grande (puede haber cambiado al clicar
   * una miniatura) y abre el lightbox con ESA imagen, no con la principal.
   */
  protected onImageGalleryZoom(index: number): void {
    if (!this.product()) return;
    this.selectedImageIndex.set(index);
    this.lightboxOpen.set(true);
  }

  protected onLightboxClose(): void {
    this.lightboxOpen.set(false);
  }

  protected onZoomClose(): void {
    this.zoomProduct.set(null);
  }

  /**
   * "Leer más" y "Ver ficha completa" del quickview abierto desde related /
   * frequently-bought-together: cierra el modal y navega al detalle del
   * producto hovereado, reutilizando la misma resolución canónica que el
   * card-click normal.
   */
  protected onZoomViewDetails(product: Product): void {
    this.zoomProduct.set(null);
    this.onRelatedCardClick(product);
  }

  protected onZoomAddToCart(event: { product: Product; quantity: number }): void {
    // Precompras: abrir el modal con el qty del zoom, sin tocar el carrito.
    if (event.product.isPreSale) {
      this.zoomProduct.set(null);
      this.prepurchaseModalService.open(event.product, event.quantity);
      return;
    }
    const qty = this.quantity();
    this.quantity.set(event.quantity);
    this.onAddToCart();
    this.quantity.set(qty);
    this.zoomProduct.set(null);
  }

  /**
   * `reserveRequest` del ProductZoomModal — el zoom ya detectó que el producto
   * es pre-sale y emite este evento en vez de `addToCart`.
   */
  protected onZoomReserve(event: { product: Product; quantity: number }): void {
    this.zoomProduct.set(null);
    this.prepurchaseModalService.open(event.product, event.quantity);
  }

  protected onZoomNotify(product: Product): void {
    // Si el zoom viene del producto principal, reusar el form inline de la PDP
    // (mejor UX: un click menos y el campo email ya está visible).
    if (this.product()?.id === product.id) {
      this.onNotifyRequest();
      return;
    }
    // Si viene de un related/FBT, abrir el modal compartido para que el usuario
    // tenga feedback claro y persista el token.
    this.stockAlertModal.open(product);
  }

  /**
   * «Ya no me avises» desde una card de relacionados/FBT o desde el quickview.
   * Si es el producto principal, la ficha se pone al día SOLO cuando la
   * cancelación se confirma (callback del servicio): el formulario inline
   * vuelve a su estado inicial y el `has_alert` que trajo el producto deja de
   * contar. Si falla (red, 401…), no cambia nada: el store conserva el token y
   * ficha, modal y cards siguen diciendo que el aviso está activo.
   *
   * El DELETE nunca se aborta: la baja en servidor se completa aunque el
   * usuario navegue, y el `StockAlertStore` (indexado por producto) se
   * actualiza en el servicio, fuera de este guard. Lo único que se descarta si
   * la operación quedó obsoleta (`notifyOpSeq`) es el efecto sobre la vista.
   * Solo toma número la cancelación del principal: las de relacionados no
   * tocan la vista y no deben invalidar una cancelación del principal en vuelo.
   */
  protected onZoomNotifyCancel(product: Product): void {
    if (this.product()?.id !== product.id) {
      this.stockAlertModal.cancel(product);
      return;
    }
    const op = ++this.notifyOpSeq;
    const productId = product.id;
    this.stockAlertModal.cancel(product, () => {
      if (op !== this.notifyOpSeq || this.product()?.id !== productId) return;
      this.serverAlertCancelled.set(true);
      this.notifyStatus.set('idle');
    });
  }

  /**
   * Agrega al carrito el bundle FBT (producto actual + sugeridos marcados).
   * Itera secuencialmente con `concatMap` para respetar las validaciones
   * de stock + reaplicación de promos servidor-side en cada paso. Tolera
   * fallos parciales: informa con toast cuántos entraron y cuáles no.
   */
  protected onBundleAddToCart(items: Product[]): void {
    // Defensa en profundidad (D26): ninguna precompra viaja al carrito normal.
    const cartItems = items.filter((p) => p.isPreSale !== true);
    if (this.bundleAdding() || cartItems.length === 0) return;

    this.bundleAdding.set(true);

    from(cartItems)
      .pipe(
        concatMap((p) =>
          this.cartService.addLine(Number(p.id), 1, p).pipe(
            map(() => ({ ok: true as const, product: p })),
            catchError((err: unknown) =>
              of({ ok: false as const, product: p, err }),
            ),
          ),
        ),
        toArray(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (results) => {
          this.bundleAdding.set(false);
          const succeeded = results.filter((r) => r.ok);
          const failed = results.filter((r) => !r.ok);

          if (failed.length === 0) {
            this.cartDrawerService.open();
            this.toastService.show({
              type: 'success',
              message:
                succeeded.length === 1
                  ? 'Producto agregado al carrito.'
                  : `${succeeded.length} productos agregados al carrito.`,
            });
            return;
          }

          if (succeeded.length === 0) {
            this.toastService.show({
              type: 'error',
              message: 'No pudimos agregar los productos. Intenta de nuevo.',
            });
            return;
          }

          this.cartDrawerService.open();
          const failedNames = failed.map((r) => r.product.name).join(', ');
          this.toastService.show({
            type: 'error',
            message: `Agregamos ${succeeded.length} de ${results.length}. No pudimos agregar: ${failedNames}.`,
          });
        },
        error: () => {
          this.bundleAdding.set(false);
          this.toastService.show({
            type: 'error',
            message: 'No pudimos agregar los productos. Intenta de nuevo.',
          });
        },
      });
  }

  protected onRelatedAddToCart(event: AddToCartEvent): void {
    // Si el producto relacionado está en precompra, abrir el modal en vez de
    // meter una línea al carrito (los related son ProductCard y emiten este
    // evento con `(addToCart)` — el nuevo `(reserveClick)` se conecta desde
    // la plantilla del RelatedProductsComponent).
    if (event.product.isPreSale) {
      this.prepurchaseModalService.open(event.product, event.quantity);
      return;
    }
    this.cartService
      .addLine(Number(event.product.id), event.quantity, event.product)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cartDrawerService.open(),
        error: (err) => {
          // `null` = bloqueo de Acceso VIP: el diálogo ya lo explica.
          const message = cartErrorMessage(err, 'No pudimos agregar el producto al carrito.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  /**
   * Handler para el output `reserveClick` de los ProductCard relacionados.
   * Se engancha desde el template de related-products si el componente re-emite
   * el output hacia arriba.
   */
  protected onRelatedReserve(product: Product): void {
    this.prepurchaseModalService.open(product);
  }

  protected onRelatedZoomOpen(product: Product): void {
    this.zoomProduct.set(product);
  }

  protected onRelatedCardClick(product: Product): void {
    if (!product.slug) return;
    // Derivar el slug canónico del producto contra el árbol cacheado para no
    // generar URLs inválidas (`/tienda/todos/...`). Si no resuelve, no
    // navegamos — preferible a abrir un listing vacío.
    const categorySlug = deriveProductCategorySlug(product, this.rootsSig());
    if (!categorySlug) return;
    this.router.navigate(['/tienda', categorySlug, product.slug], {
      state: { productId: product.id },
    });
  }

  protected navigateHome(): void {
    this.router.navigate(['/']);
  }

  // ── Private helpers ──────────────────────────────────────────
  private loadProduct(slug: string): void {
    this.loading.set(true);
    this.notFound.set(false);

    this.catalogService
      .getProductBySlug(slug)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (product) => {
          this.product.set(product);
          this.cacheSlug(product);
          this.applySeo(product);
          this.loading.set(false);
        },
        error: (err) => {
          if (err instanceof ProductNotFoundError) {
            this.handleNotFound(slug, err.reason);
          } else {
            // Error no esperado: tratar como not_found genérico para no bloquear UI.
            this.notFoundReason.set('not_found');
            this.notFound.set(true);
            this.loading.set(false);
          }
        },
      });
  }

  /**
   * Si la URL trae `state.productId` (pasado por el listing) o tenemos el id
   * en sessionStorage (el usuario ya vio este producto), intentamos resolver
   * por id y redirigir al slug nuevo. Sólo aplica cuando la razón es
   * 'not_found' (slug huérfano tras rename); 'disabled' implica producto
   * apagado y no tiene sentido redirigir.
   */
  private handleNotFound(slug: string, reason: 'not_found' | 'disabled'): void {
    this.notFoundReason.set(reason);

    if (reason === 'disabled') {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }

    const productId = this.resolveProductIdForSlug(slug);
    if (productId == null) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }

    this.catalogService
      .getProductById(productId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fresh) => {
          if (!fresh.slug || fresh.slug === slug) {
            this.notFound.set(true);
            this.loading.set(false);
            return;
          }
          // Preferimos el slug canónico del producto fresco (validado contra
          // el árbol cacheado) sobre el `:category` de la URL, que puede ser
          // virtual o inválido. Si no se puede resolver, mantenemos el slug
          // de URL como último recurso (mejor que romper la navegación).
          const canonical = deriveProductCategorySlug(fresh, this.rootsSig());
          const category = canonical
            || (this.route.snapshot.params['category'] as string | undefined)
            || 'tienda';
          this.router.navigate(['/tienda', category, fresh.slug], { replaceUrl: true });
        },
        error: () => {
          this.notFound.set(true);
          this.loading.set(false);
        },
      });
  }

  private resolveProductIdForSlug(slug: string): number | null {
    // 1. history.state.productId (pasado por el listing en navigate())
    if (isPlatformBrowser(this.platformId)) {
      const stateId = (history.state && (history.state as { productId?: unknown }).productId);
      if (typeof stateId === 'number' && Number.isFinite(stateId)) return stateId;
    }
    // 2. Cache sessionStorage poblado cuando cargamos productos con éxito.
    const cache = this.readSlugCache();
    for (const [id, s] of Object.entries(cache)) {
      if (s === slug) return Number(id);
    }
    return null;
  }

  private cacheSlug(product: Product): void {
    if (!isPlatformBrowser(this.platformId) || !product.slug) return;
    const cache = this.readSlugCache();
    cache[String(product.id)] = product.slug;
    try {
      sessionStorage.setItem(SLUG_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // sessionStorage puede fallar en modo privado — ignorar.
    }
  }

  private readSlugCache(): Record<string, string> {
    if (!isPlatformBrowser(this.platformId)) return {};
    try {
      const raw = sessionStorage.getItem(SLUG_CACHE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  private resetTransientState(): void {
    // Invalida toda operación de aviso en vuelo: su respuesta ya no es de esta vista.
    this.notifyOpSeq++;
    this.quantity.set(1);
    this.zoomProduct.set(null);
    this.lightboxOpen.set(false);
    this.selectedImageIndex.set(0);
    this.notifyStatus.set('idle');
    this.optimisticExtraSubscribers.set(0);
    this.serverAlertCancelled.set(false);
    this.addStatus.set('idle');
    this.addErrorMessage.set(null);
    if (isPlatformBrowser(this.platformId)) {
      this.document.defaultView?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }

  private applySeo(product: Product): void {
    // `description` puede traer HTML rico de Quill — limpiar antes de meterlo
    // en meta tags y JSON-LD para no exponer marcado en SEO/preview.
    const plainDescription = product.description ? stripHtml(product.description) : '';
    const description = product.shortDescription
      || plainDescription
      || 'Carta y accesorios TCG en Perrón Store.';

    const productJsonLd = {
      '@type': 'Product',
      name: product.name,
      description: product.shortDescription ?? plainDescription,
      image: product.imageUrl || undefined,
      sku: product.reference ?? String(product.id),
      offers: {
        '@type': 'Offer',
        priceCurrency: 'CRC',
        price: product.price,
        availability: (product.stock ?? 0) > 0
          ? 'https://schema.org/InStock'
          : product.prepurchase && getPrepurchaseStock(product) > 0
            ? 'https://schema.org/PreOrder'
            : 'https://schema.org/OutOfStock',
        url: absoluteUrl(this.router.url.split('?')[0]),
      },
    };
    const breadcrumbJsonLd = buildBreadcrumbJsonLd(this.breadcrumbs());

    this.seoService.apply({
      title: `${product.name} · Perrón Store`,
      rawTitle: true,
      description,
      image: product.imageUrl,
      type: 'product',
      canonical: this.router.url.split('?')[0],
      jsonLd: [productJsonLd, breadcrumbJsonLd],
    });
  }
}
